'use strict';

import {
    unixTimestamp,
    getErrorsOnPage,
    isEmpty
} from "./Utils";
import {
    GlobalMetricKeys,
    GlobalMetricLabels
} from './AppConstants';

/*
 * Base analytics Store for handling analytics data. The apps will extend this store for their analytics needs.
 *
 * Before you start adding labels to events make sure you understand cardinality (both potential and actual) or at least
 * talk it over with someone who does. Otherwise, what you do could cause problems in the Prometheus database.
 *
 * Events:
 *
 * To submit information about a single event ( i.e. Button X was pushed ), use the addEvent method. This will result
 * in a counter metric with the name 'ui_event' having a label, 'action', which has the value of the given key. If
 * additional metadata about the event is desired you can pass a map where the label name is the key (and the value is
 * the value). You should, however, only use the labels from constants defined in the collective specific react library
 * (e.g. lib-react-myplan/lib/MetricsLabels) and be very careful to keep the values consistent across actions that use
 * the same labels. If you don't you run the risk of causing an adverse cardinality event and crushing the Prometheus
 * database.
 *
 * For per-event metadata that should simply like logged and without becoming part of the Prometheus event you can
 * pass a JSON object in the additionalData arg.
 *
 * Timers:
 *
 * To track how long some actions takes to happen use the startEvent and endEvent methods. Metadata follows the
 * same scheme as regular events. Used the 'labels' argument to add labels. Use the additionalData arg to put
 * event specific data into logs, but not into the event.
 *
 * Events are only forwarded to the backend if the application is configured to have profiling enabled.
 */
class BaseAnalyticsStore {

    constructor(appStore, httpClient) {
        this.appStore = appStore;
        this.httpClient = httpClient;
        // Default the buffer to 0 on construction so that any events that are generated before the AppStore is fully
        // initialized have a chance to be forwarded to the backend.
        this.bufferSize = 0;
        this.isDebug = false;

        // Storage for "timer" events.
        this.timerEvents = {};
        // Storage for events before they are flushed.
        this.eventBuffer = [];

        /*
         * A list of functions that transform the event
         * Each function should take in the event string and return the modified event.
         */
        this.eventFilters = [];
        // Register a filter to scrap "reg banner" errors off of the page ...
        this.registerEventFilter((event) => {
            let errorsOnPage = getErrorsOnPage(document);
            if (errorsOnPage) {
                // Every event should have an additionalData property by now. Add the errorsOnPage value to it.
                event.additionalData.errorOnPage = errorsOnPage;
            }
            return event;
        });
    }

    /**
     * Queue an analytics event.
     *
     * @param eventKey The event name.
     * @param labels A map where the key is a label name and the value is the label value.
     * @param additionalData A JSON object containing metadata about the event that will only be logging.
     *
     * @return The event object.
     */
    addEvent(eventKey, labels, additionalData) {
        return this._queueEvent(eventKey, labels, additionalData);
    }

    /**
     * Put a metric event into the backend buffer.
     * @key The metric key name
     * @labels A map of labels.
     * @additionalData A map of additional data.
     * @param duration For timer events only. The duration of the event is milliseconds.
     * @private
     */
    _queueEvent(key, labels, additionalData, duration) {
        labels = labels ?? {};
        additionalData = additionalData ?? {};

        // Add the Application label ...
        labels[GlobalMetricLabels.application] = this.appStore.getApplicationName() ?? "unknown";
        labels[GlobalMetricLabels.environment] = this.appStore.getEnvironment() ?? "unknown";

        let event = {
            level: "metrics", // Given the backend processor something easy to select on
            timestamp: unixTimestamp(),
            metricKey: key,
            labels: labels,
            additionalData: additionalData,
            applicationData: {
                version: this.appStore.getApplicationVersion(),
                checksum: this.appStore.getChecksum(),
                instance: this.appStore.getHostname(),
                user: this.appStore.getUserName()
            }
        };

        // Add the duration to the event object if it is supplied.
        if (duration) {
            event.duration = duration;
        }

        // Run the event through the event filters ...
        this.eventFilters.forEach(filter => event = filter(event));

        this.eventBuffer.push(event);

        // See if it's time to flush to the backend ...
        if (this.eventBuffer.length > this.bufferSize) {
            this.flush();
        }

        return event;
    }

    /**
     * A convenience method for adding error events.
     * @param level The logging level as see in Logger.LoggingLevels.
     * @param message The error text.
     * @param additionalData A JSON object containing additional data about the error.
     * @returns The event object.
     */
    addError(level, message, additionalData, expected) {
        let data = {
                msg: message ?? "undefined",
                ...additionalData};

        // Send to the backend ...
        const labels = this.label(GlobalMetricLabels.loggingLevel, level)
            .add(GlobalMetricLabels.expected, expected).get();
        // Return the event. Mostly so that Logger can log it if it wants to.
        return this.addEvent(GlobalMetricKeys.error, labels, data);
    }

    /**
     * Signal the start of a timer event. Additional data should be passed in the endTimer() method because
     * often the values aren't know until the operation completes.
     *
     * Event keys are determined in collaboration with UX, has business significance and are documented
     * in the wiki, so don't just go makin'em up.
     *
     * @param key The timer event name.
     * @param labels A map where the key is a label name and the value is the label value.
     */
    startTimer(key, labels) {
        if (!isEmpty(key)) {
            this.timerEvents[key] = {
                labels: labels,
                startTime: Date.now()
            }
        }
    }

    /**
     * Signal the end of a previously started timer event.
     *
     * @param key The event key.
     * @param additionalData A JSON object containing metadata about the event that will only be logging.
     */
    endTimer(key, additionalData) {
        if (this.timerEvents[key]) {
            const startEntry = this.timerEvents[key];
            const duration = Date.now() - startEntry.startTime;
            // Remove the start timer ...
            delete this.timerEvents[key];
            return this._queueEvent(key, startEntry.labels, additionalData, duration);
        } else {
            const msg = `attempted to stop timer, but found no entry for [${key}].`
            this.addError("warn", msg);
            console.warn(msg);
        }
    }

    /**
     * Empty the event buffer.
     * @private
     */
    _clearEventBuffer() {
        this.eventBuffer = [];
    }

    /**
     * Flushes buffered events to the backend.
     *
     * If you'd like to flush asynchronously, call this function like normal.
     * If you'd like to block until the flush is finished (e.g. don't run a logout until flush is finished) call this
     * like ... await analyticsStore.flush()
     */
    async flush() {
        // Don't attempt to flush if there aren't any items to flush.
        if (this.eventBuffer.length === 0 ) {
            return;
        }

        const body = JSON.stringify(this.eventBuffer);
        this._clearEventBuffer();

        if ( this.appStore.isAnalyticsEnabled() ) {
            try {
                await this.httpClient.doPost("api/analytics", body, "analytics flush");
                if (this.isDebug) {
                    console.debug("AnalyticsStore: Events flushed.");
                }
            } catch (error) {
                console.log("AnalyticsStore: Failed to flush events: " + error);
                if (this.isDebug) {
                    console.debug("AnalyticsStore: Dumping events from failed flush to console: " + body);
                }
            }
        }
    }

    label(key, value) {
        return new LabelMaker(key, value);
    }

    /**
     * Adds a filter function to transform generated events.
     * Each function should take in the event string and return the modified event.
     */
    registerEventFilter(filter) {
        this.eventFilters.push(filter);
    }

    setHttpClient(httpClient) {
        this.httpClient = httpClient;
    }

    setBufferSize(bufferSize) {
        this.bufferSize = bufferSize;
    }

    setDebug(isDebug) {
        this.isDebug = isDebug;
    }
}

/**
 * A "fluent interface" for making label objects.
 */
class LabelMaker {
    constructor(key, value) {
        this.labelObject = {};
        this.labelObject[key] = value;
    }
    add(key, value) {
        this.labelObject[key] = value;
        return this;
    }
    get() {
        return this.labelObject;
    }
}

export default BaseAnalyticsStore;